import { Button, CircularProgress, Text } from '@shared/components';
import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ActionExecutionCreditsBadge } from '@shared/features/action-execution-credits';
import { TaskVisualizationSettingsPopover } from '@shared/features/TaskVisualizationSettings';
import { LineageFetchPolicy } from '@shared/generated/graphql';
import { useActionExecutions } from '@shared/graphql/hooks/actionExecutions/useActionExecutions';
import { useConversation } from '@shared/graphql/hooks/conversations/useConversation';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { DEFAULT_CHAT_VIS_SETTINGS } from '@shared/hooks/useTaskVisSettings';
import { useConciergeContext } from '@shared/plugin/contexts/ConciergeContext';
import clsx from 'clsx';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { PiArrowUpRightBold } from 'react-icons/pi';
import { useConversationIsWorking } from '../hooks/useConversationIsWorking';
import { isChatTitlePending, UNTITLED_CHAT_TITLE } from '../utils/chatTitle';
import { ChatMessageInput, MessageDraft } from './ChatMessageInput';
import { ChatTitleSkeleton } from './ChatTitleSkeleton';
import { ACTIVE_TURN_POLL_INTERVAL_MS, ChatTranscript } from './ChatTranscript';
import { OptimisticChatMessage } from './optimisticChatMessage';
import { ThreadHeader } from './ThreadHeader';

const ACTION_EXECUTIONS_FALLBACK_POLL_INTERVAL_MS = 60 * 1000;

type ChatThreadPanelProps = {
  conversationId: string;
  getTaskUrl?: (actionExecutionId: string) => string;
  getViewContext?: ChatViewContextGetter;
  initialDraft?: MessageDraft;
  inputBanner?: ReactNode;
  isCompact?: boolean;
  leadingAction?: ReactNode;
  onBack?: () => void;
  onInitialDraftConsumed?: () => void;
  showHeader?: boolean;
  trailingAction?: ReactNode;
};

export function ChatThreadPanel({
  conversationId,
  getTaskUrl,
  getViewContext,
  initialDraft,
  inputBanner,
  isCompact = false,
  leadingAction,
  onBack,
  onInitialDraftConsumed,
  showHeader = true,
  trailingAction,
}: ChatThreadPanelProps) {
  const { defaultOrgId, isVoomaAdmin } = useMeContext();
  const { hasAccessToConcierge } = useConciergeContext();
  const { track } = useAnalytics();
  const [hasMirrorableExternalChannel, setHasMirrorableExternalChannel] =
    useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<
    OptimisticChatMessage[]
  >([]);
  const [inputContainer, setInputContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [countdownSlot, setCountdownSlot] = useState<HTMLDivElement | null>(
    null
  );
  const [inputHeight, setInputHeight] = useState(0);

  useEffect(() => {
    if (!inputContainer) return;

    const observer = new ResizeObserver(() => {
      setInputHeight(inputContainer.getBoundingClientRect().height);
    });
    observer.observe(inputContainer);
    return () => observer.disconnect();
  }, [inputContainer]);
  const [isAwaitingReply, setIsAwaitingReply] = useState(false);
  const { conversation, isLoading } = useConversation({ id: conversationId });
  const isAgentRunning = useConversationIsWorking(conversationId);
  // NOTE(aman): The subscription has been missing mid-turn updates, and a turn
  // usually ends before the fallback poll fires, so poll fast while one runs.
  const { actionExecutions } = useActionExecutions({
    organizationIds: defaultOrgId ? [defaultOrgId] : [],
    conversationIds: [conversationId],
    fetchPolicy: 'cache-and-network',
    lineageFetchPolicy: LineageFetchPolicy.ALL,
    pollInterval:
      isAwaitingReply || isAgentRunning
        ? ACTIVE_TURN_POLL_INTERVAL_MS
        : ACTION_EXECUTIONS_FALLBACK_POLL_INTERVAL_MS,
    subscribeToUpdates: true,
  });
  const actionExecutionEvents = useMemo(
    () => actionExecutions.flatMap((actionExecution) => actionExecution.events),
    [actionExecutions]
  );
  const isAgentWorking = isAwaitingReply || isAgentRunning;
  const [chatActionExecution] = actionExecutions;

  useEffect(() => {
    setOptimisticMessages([]);
  }, [conversationId]);

  useEffect(() => {
    track('Viewed Chat Conversation', { conversationId });
  }, [conversationId, track]);

  const updateOptimisticMessage = useCallback(
    (message: OptimisticChatMessage) => {
      setOptimisticMessages((messages) => {
        const hasMessage = messages.some(({ id }) => id === message.id);
        return hasMessage
          ? messages.map((existingMessage) =>
              existingMessage.id === message.id ? message : existingMessage
            )
          : [...messages, message];
      });
    },
    []
  );

  const removeOptimisticMessage = useCallback((messageId: string) => {
    setOptimisticMessages((messages) =>
      messages.filter(({ id }) => id !== messageId)
    );
  }, []);

  const headerProps = { isCompact, leadingAction, onBack, trailingAction };

  if (!conversation) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {showHeader && <ThreadHeader {...headerProps} />}
        <div className="flex flex-1 items-center justify-center p-4">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Text type="body-sm" className="text-center text-gray-500">
              Could not find conversation
            </Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showHeader && (
        <ThreadHeader
          {...headerProps}
          trailingAction={
            <>
              {chatActionExecution && (
                <ActionExecutionCreditsBadge
                  actionExecutionId={chatActionExecution.id}
                  className="shrink-0"
                  ownLabel="This chat"
                />
              )}
              {getTaskUrl && chatActionExecution && (
                <Button
                  aria-label="Open task in new tab"
                  className="shrink-0"
                  icon={<PiArrowUpRightBold size={14} />}
                  isGhost
                  onPress={() =>
                    window.open(
                      getTaskUrl(chatActionExecution.id),
                      '_blank',
                      'noopener'
                    )
                  }
                  size="xs"
                  tooltip="Open task in new tab"
                />
              )}
              {isVoomaAdmin && (
                <div className="shrink-0">
                  <TaskVisualizationSettingsPopover
                    defaultVisSettings={DEFAULT_CHAT_VIS_SETTINGS}
                  />
                </div>
              )}
              {trailingAction}
            </>
          }
        >
          {isChatTitlePending(
            conversation.title,
            new Date(conversation.createdAt)
          ) ? (
            <div className="flex min-w-0 flex-1 items-center">
              <ChatTitleSkeleton className="w-48" />
            </div>
          ) : (
            <Text type="body-sm" className="text-dark min-w-0 flex-1 truncate">
              {conversation.title?.trim() || UNTITLED_CHAT_TITLE}
            </Text>
          )}
        </ThreadHeader>
      )}
      <div className="relative min-h-0 flex-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-10 bg-gradient-to-b from-white to-transparent"
        />
        <div
          ref={setScrollContainer}
          className={clsx(
            // NOTE(wassim): without `relative`, absolutely positioned descendants
            // (`sr-only`) anchor to the wrapper and escape this container's clip.
            'relative h-full overflow-y-auto pt-4',
            isCompact ? 'px-4' : 'px-20'
          )}
          style={{
            paddingBottom: hasAccessToConcierge ? inputHeight + 24 : 24,
          }}
        >
          <div className="mx-auto max-w-3xl">
            <ChatTranscript
              conversationId={conversationId}
              actionExecutionEvents={actionExecutionEvents}
              countdownSlot={countdownSlot}
              isAgentWorking={isAgentWorking}
              onAwaitingReplyChange={setIsAwaitingReply}
              onExternalChannelAvailabilityChange={
                setHasMirrorableExternalChannel
              }
              onOptimisticMessagePersisted={removeOptimisticMessage}
              optimisticMessages={optimisticMessages}
              scrollContainer={scrollContainer}
            />
          </div>
        </div>
        <div
          className={clsx(
            'pointer-events-none absolute inset-x-0 z-20',
            isCompact ? 'px-4' : 'px-20'
          )}
          style={{
            bottom: hasAccessToConcierge ? inputHeight + 12 : 16,
          }}
        >
          <div
            ref={setCountdownSlot}
            className="mx-auto flex max-w-3xl justify-center"
          />
        </div>
        {hasAccessToConcierge && (
          <div
            ref={setInputContainer}
            className={clsx(
              'absolute inset-x-0 bottom-0',
              isCompact ? 'px-4 pb-4' : 'px-20 pb-6'
            )}
          >
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
              {inputBanner}
              <div className="rounded-lg shadow-lg">
                <ChatMessageInput
                  key={conversationId}
                  conversationId={conversationId}
                  getViewContext={getViewContext}
                  hasMirrorableExternalChannel={hasMirrorableExternalChannel}
                  initialDraft={initialDraft}
                  organizationId={conversation.organizationId}
                  onInitialDraftConsumed={onInitialDraftConsumed}
                  onOptimisticMessageRemove={removeOptimisticMessage}
                  onOptimisticMessageUpdate={updateOptimisticMessage}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
